import React from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { useUser } from '@paintscout/ui-business'

/**
 * Guard component that redirects users on an inactive company to the billing page.
 *
 * Fires for any company that is not subscribed (`!isSubscribed`) — this covers the
 * strict churned case (cancelled + term ended) as well as a company simply flagged
 * inactive (e.g. `company.active === false` set by the Chargebee cancellation webhook,
 * regardless of subscription status or term end). Only settings/billing routes are
 * allowed through so the user can still reach billing to reactivate. nonBilling
 * companies and active trials are subscribed, so they pass through untouched;
 * trial-expired users are handled separately by TrialExpiredGuard.
 */
export default function ChurnedGuard({ children }: { children: React.ReactNode }) {
  const { isSubscribed, isSuperadmin, isSupport } = useUser()
  const location = useLocation()

  // Allow access to settings/billing routes when inactive
  const isBillingRoute = location.pathname.startsWith('/settings')

  // If the company is inactive and not on a billing route, redirect to billing.
  // Superadmin and support bypass this block as they are internal staff.
  if (!isSubscribed && !isBillingRoute && !isSuperadmin && !isSupport) {
    return <Redirect to="/settings?section=billing-plan" />
  }

  return <>{children}</>
}
